import React, { useEffect, useState } from 'react';
import styles from './PromoBlocks.module.css';
import Button from '../UI/Button/Button';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import { api } from '../../utils/api';
import { transformPromoBlocks } from '../../utils/transformers';
import { getOptimalTextColor } from '../../utils/brightnessDetection';
import { CONSTRUCTION_PROGRESS_PAGE } from '../../router/paths';
import ConstructionProgressImage from '../../assets/images/ProjectStatusPage/PreviewImage.webp';

interface PromoBlockData {
  id: number;
  type: 'big' | 'small';
  title: string;
  text: string;
  link: string;
  bgColor: string;
  image: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  displayType?: 'big' | 'small' | 'full_width';
}

const constructionProgressBlock: PromoBlockData = {
  id: -1000,
  type: 'small',
  displayType: 'small',
  title: 'Продвижение объекта',
  text: 'Смотреть ход работ',
  link: CONSTRUCTION_PROGRESS_PAGE,
  bgColor: '#ECECEC',
  image: ConstructionProgressImage,
  backgroundSize: '130% auto',
  backgroundPosition: 'center 30%',
};

type BlockCardProps = {
  block: PromoBlockData;
};

const BlockCard: React.FC<BlockCardProps> = ({ block }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [textColor, setTextColor] = useState('#ffffff');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth);
    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, []);

  useEffect(() => {
    const analyzeBackground = async () => {
      setIsAnalyzing(true);
      
      try {
        const optimalColor = await getOptimalTextColor(
          block.image && block.image.trim() ? block.image : undefined,
          block.bgColor && block.bgColor.trim() ? block.bgColor : undefined,
          0.7
        );
        
        setTextColor(optimalColor);
      } catch (error) {
        console.warn('Failed to analyze background brightness:', error);
        setTextColor('#ffffff');
      } finally {
        setIsAnalyzing(false);
      }
    };

    if ((block.image && block.image.trim()) || (block.bgColor && block.bgColor.trim())) {
      analyzeBackground();
    } else {
      setTextColor('#ffffff');
    }
  }, [block.image, block.bgColor]);

  const buttonStyle = {
    width: windowWidth <= 710 ? '100%' : 'fit-content',
  };

  const backgroundStyle: React.CSSProperties = block.image && block.image.trim()
    ? {
        backgroundImage: `url(${block.image})`,
        backgroundPosition: block.backgroundPosition || 'center',
        backgroundSize: block.backgroundSize || 'cover',
        backgroundRepeat: 'no-repeat'
      }
    : block.bgColor && block.bgColor.trim()
    ? {
        backgroundColor: block.bgColor
      }
    : {
        backgroundColor: '#131313'
      };

  const textStyle: React.CSSProperties = {
    color: textColor,
    transition: 'color 0.3s ease-in-out',
  };

  const titleStyle: React.CSSProperties = {
    ...textStyle,
    opacity: isAnalyzing ? 0.8 : 1,
    transition: 'color 0.3s ease-in-out, opacity 0.2s ease-in-out',
  };

  const effectiveType = block.displayType || block.type;
  const cardTypeClass = effectiveType === 'big' 
    ? styles.big_card 
    : effectiveType === 'small' 
    ? styles.small_card 
    : styles.full_width_card;

  return (
    <div 
      className={`${styles.card} ${cardTypeClass}`}
      style={backgroundStyle}
    >
      <h3 style={titleStyle}>
        {block.title || 'Заголовок отсутствует'}
      </h3>
      {block.link && block.link.trim() ? (
        <Link to={block.link} className={styles.button_wrapper}>
          <Button roundType='small_rounded' paddingType='big' style={buttonStyle}>
            {block.text || 'Кнопка'}
          </Button>
        </Link>
      ) : (
        <Button roundType='small_rounded' paddingType='big' style={buttonStyle}>
          {block.text || 'Кнопка'}
        </Button>
      )}
    </div>
  );
};

const PromoBlocksSkeleton: React.FC = () => (
  <div className={styles.wrapper}>
    <div className={styles.top}>
      <div className={`${styles.card} ${styles.skeleton}`} />
    </div>
    <div className={styles.status}>
      <div className={styles.content}>
        <div className={styles.text_wrapper}>
          <h4>В&nbsp;наших соцсетях&nbsp;&mdash; все самое актуальное</h4>
        </div>
        <div className={styles.buttons_wrapper}>
          <Link className={styles.button} to={'https://t.me/vostok_stroy'} target='_blank'>
            <Icon name='telegramIcon'/>
          </Link>
          <Link className={styles.button} to={'https://vk.com/vostok_stroy_kaspiysk'} target='_blank'>
            <Icon name='vkIcon'/>
          </Link>
          <Link className={styles.button} to={'https://www.youtube.com/@vostok_stroy'} target='_blank'>
            <Icon name='youtubeIcon'/>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const PromoBlocksError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className={styles.wrapper}>
    <div className={styles.top}>
      <BlockCard block={constructionProgressBlock} />
    </div>
    <div className={styles.error}>
      <p>Не удалось загрузить промо-блоки</p>
      <Button roundType='small_rounded' paddingType='big' onClick={onRetry}>
        Попробовать снова
      </Button>
    </div>
    <div className={styles.status}>
      <div className={styles.content}>
        <div className={styles.text_wrapper}>
          <h4>В&nbsp;наших соцсетях&nbsp;&mdash; все самое актуальное</h4>
        </div>
        <div className={styles.buttons_wrapper}>
          <Link className={styles.button} to={'https://t.me/vostok_stroy'} target='_blank'>
            <Icon name='telegramIcon'/>
          </Link>
          <Link className={styles.button} to={'https://vk.com/vostok_stroy_kaspiysk'} target='_blank'>
            <Icon name='vkIcon'/>
          </Link>
          <Link className={styles.button} to={'https://www.youtube.com/@vostok_stroy'} target='_blank'>
            <Icon name='youtubeIcon'/>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const PromoBlocks: React.FC = () => {
  const [promoBlocks, setPromoBlocks] = useState<PromoBlockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPromoBlocks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.promoBlocks.getAll();
      const transformedBlocks = transformPromoBlocks(response);
      
      const validBlocks = transformedBlocks.filter(block => 
        block && 
        typeof block.id === 'number' && 
        typeof block.title === 'string' && 
        typeof block.text === 'string' &&
        (block.type === 'big' || block.type === 'small')
      );
      
      setPromoBlocks(validBlocks);
    } catch (err) {
      console.error('Failed to fetch promo blocks:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoBlocks();
  }, []);

  const handleRetry = () => {
    fetchPromoBlocks();
  };

  if (loading) {
    return <PromoBlocksSkeleton />;
  }

  if (error) {
    return <PromoBlocksError onRetry={handleRetry} />;
  }

  const processedBlocks = (() => {
    if (promoBlocks.length === 0) return [];
    
    const blocks = [...promoBlocks];
    
    if (blocks.length === 1) {
      return [{
        ...blocks[0],
        displayType: 'full_width' as const
      }];
    }
    
    if (blocks.length === 2) {
      const [first, second] = blocks;
      
      if (first.type === 'big' && second.type === 'big') {
        return [
          { ...first, displayType: 'small' as const },
          { ...second, displayType: 'big' as const }
        ];
      }
      
      if (first.type === 'small' && second.type === 'small') {
        return [
          { ...first, displayType: 'small' as const },
          { ...second, displayType: 'big' as const }
        ];
      }
      
      const smallBlock = first.type === 'small' ? first : second;
      const bigBlock = first.type === 'big' ? first : second;
      
      return [
        { ...smallBlock, displayType: smallBlock.type },
        { ...bigBlock, displayType: bigBlock.type }
      ];
    }
    
    const sortedBlocks = blocks.sort((a, b) => {
      if (a.type === 'small' && b.type === 'big') return -1;
      if (a.type === 'big' && b.type === 'small') return 1;
      return 0;
    });
    
    return sortedBlocks.map(block => ({
      ...block,
      displayType: block.type
    }));
  })();
  const blocksToRender = [constructionProgressBlock, ...processedBlocks];

  return (
    <div className={styles.wrapper}>
      <div className={styles.top}>
        {blocksToRender.map((block) => (
          <BlockCard key={block.id} block={block} />
        ))}
      </div>
      <div className={styles.status}>
        <div className={styles.content}>
          <div className={styles.text_wrapper}>
            <h4>В&nbsp;наших соцсетях&nbsp;&mdash; все самое актуальное</h4>
          </div>
          <div className={styles.buttons_wrapper}>
            <Link className={styles.button} to={'https://t.me/vostok_stroy'} target='_blank'>
              <Icon name='telegramIcon'/>
            </Link>
            <Link className={styles.button} to={'https://vk.com/vostok_stroy_kaspiysk'} target='_blank'>
              <Icon name='vkIcon'/>
            </Link>
            <Link className={styles.button} to={'https://www.youtube.com/@vostok_stroy'} target='_blank'>
              <Icon name='youtubeIcon'/>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBlocks;
